'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { Suspense, useState, useTransition, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { MarketingShowcaseMediaAsset } from './content';

export function LandingShowcaseCarousel({
  assets,
  alt,
  fallbackTitle,
  fallbackDescription,
  previousLabel,
  nextLabel,
}: {
  assets: MarketingShowcaseMediaAsset[];
  alt: string;
  fallbackTitle: string;
  fallbackDescription: string;
  previousLabel: string;
  nextLabel: string;
}) {
  return (
    <ShowcaseCarousel
      slides={assets.map((asset, index) => ({
        id: `${asset.url}-${index}`,
        label: asset.label ?? String(index + 1).padStart(2, '0'),
        content: (
          <div className="bg-marketing-accent-subtle grid min-h-112 overflow-hidden rounded-2xl md:grid-cols-2 md:rounded-3xl">
            <div className="flex min-w-0 flex-col justify-center px-7 py-10 text-left sm:px-10 md:px-12 lg:px-16">
              <p className="text-marketing-fg-subtle text-[clamp(0.6875rem,0.9vw,0.8125rem)] leading-[1.3] font-semibold [letter-spacing:-0.01em]">
                {asset.label ?? String(index + 1).padStart(2, '0')}
              </p>
              <h3 className="text-marketing-fg mt-4 max-w-lg text-[clamp(1.5rem,3.4vw,2.75rem)] leading-[1.1] font-bold [letter-spacing:-0.03em] text-balance">
                {asset.title?.trim() || fallbackTitle}
              </h3>
              <p className="text-marketing-fg-muted mt-4 max-w-md text-[clamp(0.9375rem,1.3vw,1.25rem)] leading-[1.35] font-semibold [letter-spacing:-0.015em] text-pretty">
                {asset.description?.trim() || fallbackDescription}
              </p>
            </div>

            <div className="relative min-h-72 md:min-h-full">
              <Image
                src={asset.url}
                alt={alt}
                fill
                unoptimized
                sizes="(min-width: 768px) 50vw, 100vw"
                className="object-cover"
              />
            </div>
          </div>
        ),
      }))}
      label={alt}
      previousLabel={previousLabel}
      nextLabel={nextLabel}
    />
  );
}

export type ShowcaseSlide = { id: string; label: string; content: ReactNode };

export function ShowcaseCarousel({
  slides,
  label,
  roleDescription = 'carousel',
  previousLabel,
  nextLabel,
}: {
  slides: ShowcaseSlide[];
  roleDescription?: string;
  label: string;
  previousLabel: string;
  nextLabel: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [, startTransition] = useTransition();
  const activeSlide = slides[activeIndex] ?? slides[0];

  if (!activeSlide) return null;

  // Slide scenes load lazily (next/dynamic). Switching inside a transition
  // keeps the current slide on screen until the next one has loaded; a plain
  // update would suspend up to the route's loading.tsx, blanking the page and
  // resetting its scroll like a reload (#5772).
  const select = (next: (index: number) => number) => {
    startTransition(() => setActiveIndex(next));
  };
  const selectPrevious = () => {
    select((index) => Math.max(0, index - 1));
  };
  const selectNext = () => {
    select((index) => Math.min(slides.length - 1, index + 1));
  };

  return (
    <div
      role="region"
      aria-roledescription={roleDescription}
      aria-label={label}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'ArrowLeft') selectPrevious();
        if (event.key === 'ArrowRight') selectNext();
      }}
    >
      <div className="mb-6 flex flex-wrap justify-center gap-2 md:mb-8 md:gap-3">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => select(() => index)}
            aria-current={index === activeIndex ? 'true' : undefined}
            className={cn(
              'cursor-pointer rounded-md px-4 py-2 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] font-normal transition-colors md:px-5',
              index === activeIndex
                ? 'bg-marketing-fg text-marketing-bg'
                : 'bg-marketing-accent-subtle text-marketing-accent-fg hover:bg-marketing-accent'
            )}
          >
            {slide.label}
          </button>
        ))}
      </div>

      {/* Backstop: a slide that still suspends waits here, never in the route. */}
      <Suspense fallback={<div aria-busy="true" className="min-h-112" />}>
        <div
          key={activeSlide.id}
          aria-label={`${label} ${activeIndex + 1}/${slides.length}`}
        >
          {activeSlide.content}
        </div>
      </Suspense>

      <div className="mt-5 flex items-center justify-between px-2 md:mt-6">
        <button
          type="button"
          onClick={selectPrevious}
          disabled={activeIndex === 0}
          className="text-marketing-fg group flex cursor-pointer items-center gap-2 text-[clamp(0.8rem,0.9vw,0.85rem)] leading-[1.45] font-medium disabled:cursor-default disabled:opacity-35"
        >
          <span className="bg-marketing-accent-subtle group-enabled:group-hover:bg-marketing-accent flex size-7 items-center justify-center rounded-md transition-[background-color,translate] duration-200 ease-out motion-safe:group-enabled:group-hover:-translate-x-[4px] motion-reduce:transition-none">
            <ArrowLeft className="size-4" aria-hidden="true" />
          </span>
          <span>{previousLabel}</span>
        </button>

        <button
          type="button"
          onClick={selectNext}
          disabled={activeIndex === slides.length - 1}
          className="text-marketing-fg group flex cursor-pointer items-center gap-2 text-[clamp(0.8rem,0.9vw,0.85rem)] leading-[1.45] font-medium disabled:cursor-default disabled:opacity-35"
        >
          <span>{nextLabel}</span>
          <span className="bg-marketing-accent-subtle group-enabled:group-hover:bg-marketing-accent flex size-7 items-center justify-center rounded-md transition-[background-color,translate] duration-200 ease-out motion-safe:group-enabled:group-hover:translate-x-[4px] motion-reduce:transition-none">
            <ArrowRight className="size-4" aria-hidden="true" />
          </span>
        </button>
      </div>
    </div>
  );
}
